import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { StatCard, SectionTitle, Empty } from "@/components/GlassBits";
import { formatINR } from "@/lib/format";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar,
  PieChart, Pie, Cell, AreaChart, Area, CartesianGrid, Legend
} from "recharts";
import { TrendingUp, Wallet, Clock, RefreshCcw, Sparkles } from "lucide-react";
import { sounds } from "@/lib/sound";

const CHART_COLORS = ["#fbce18", "#f59e0b", "#10b981", "#3b82f6", "#a855f7", "#ef4444"];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [pulse, setPulse] = useState(0);
  const lastTxCount = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        api.get("/admin/dashboard/stats"),
        api.get("/admin/dashboard/charts"),
      ]);
      const newCount = s.data.total_transactions;
      // Play a sound only when the counter grows (skip on very first load)
      if (lastTxCount.current !== null && typeof newCount === "number" && newCount > lastTxCount.current) {
        sounds.payment();
      }
      lastTxCount.current = newCount;
      setStats(s.data);
      setCharts(c.data);
      setPulse((p) => p + 1);
    } catch (e) {
      console.error("[AdminDashboard] refresh failed", e);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  if (!stats) return <div className="text-white/50">Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#fbce18]/80">Finance Overview</div>
          <h1 className="mt-1 font-display text-3xl tracking-tight">Realtime dashboard</h1>
        </div>
        <div className="text-xs text-white/50 inline-flex items-center gap-2">
          <span className={`w-1.5 h-1.5 rounded-full bg-[#fbce18] ${pulse ? "animate-ping" : ""}`}></span>
          Auto-refresh · 5s
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard testid="stat-total" label="Total Earnings" value={formatINR(stats.total_earnings)} sub={`${stats.total_transactions} transactions`} accent />
        <StatCard testid="stat-today" label="Today" value={formatINR(stats.today_earnings)} sub={`${stats.today_transactions} today`} />
        <StatCard testid="stat-month" label="This Month" value={formatINR(stats.monthly_earnings)} sub={`${stats.monthly_transactions} this month`} />
        <StatCard testid="stat-pending" label="Pending" value={formatINR(stats.pending_amount)} sub={`${stats.pending_count} outstanding`} />
        <StatCard testid="stat-refund" label="Refunded" value={formatINR(stats.refund_amount)} sub={`${stats.refund_count} refunds`} />
      </div>

      {charts && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 ldc-glass p-5">
            <SectionTitle eyebrow="Revenue" title="Last 14 days" right={<span className="ldc-badge ldc-badge-pending"><TrendingUp size={12}/> live</span>} />
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.revenue}>
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#fbce18" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#fbce18" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" tickFormatter={(v) => v.slice(5)} fontSize={11} />
                  <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} />
                  <Tooltip contentStyle={{ background: "rgba(15,15,15,0.95)", border: "1px solid rgba(251,206,24,0.3)", borderRadius: 8 }} />
                  <Area type="monotone" dataKey="amount" stroke="#fbce18" strokeWidth={2} fill="url(#g1)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="ldc-glass p-5">
            <SectionTitle eyebrow="Methods" title="Payment mix" />
            {charts.methods.length === 0 ? (
              <div className="text-sm text-white/40 py-10 text-center">No payments yet</div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={charts.methods} dataKey="total" nameKey="method" innerRadius={50} outerRadius={90} paddingAngle={4}>
                {charts.methods.map((m, i) => (<Cell key={m.method || `m-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
                    </Pie>
                    <Tooltip contentStyle={{ background: "rgba(15,15,15,0.95)", border: "1px solid rgba(251,206,24,0.3)", borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="ldc-glass p-5">
            <SectionTitle eyebrow="Growth" title="Last 6 months" />
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.growth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="month" stroke="rgba(255,255,255,0.4)" fontSize={11} />
                  <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} />
                  <Tooltip contentStyle={{ background: "rgba(15,15,15,0.95)", border: "1px solid rgba(251,206,24,0.3)", borderRadius: 8 }} />
                  <Bar dataKey="amount" fill="#fbce18" radius={[6,6,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="ldc-glass p-5">
            <SectionTitle eyebrow="Categories" title="Branch earnings" />
            {stats.category_stats.length === 0 ? (
              <div className="text-sm text-white/40 py-10 text-center">Awaiting first capture</div>
            ) : (
              <div className="space-y-3 max-h-56 overflow-y-auto ldc-scroll">
                {stats.category_stats.map((c, i) => (
                  <div key={c.branch} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span className="text-white/80">{c.branch}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{formatINR(c.total)}</div>
                      <div className="text-[10px] text-white/40">{c.count} tx</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="ldc-glass p-5">
            <SectionTitle eyebrow="Websites" title="Revenue split" />
            {charts.websites.length === 0 ? (
              <div className="text-sm text-white/40 py-10 text-center">Connect a website to see split</div>
            ) : (
              <div className="space-y-3 max-h-56 overflow-y-auto ldc-scroll">
                {charts.websites.map((w, i) => (
                  <div key={w.website + i} className="flex items-center justify-between text-sm">
                    <div className="text-white/80">{w.website}</div>
                    <div className="text-right">
                      <div className="font-medium">{formatINR(w.total)}</div>
                      <div className="text-[10px] text-white/40">{w.count} tx</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-8 ldc-glass p-6 relative overflow-hidden">
        <div className="absolute inset-0 ldc-shine opacity-40 pointer-events-none"></div>
        <div className="relative z-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#fbce18]/80">Getting started</div>
            <h3 className="mt-2 font-display text-xl tracking-tight">Create your first Razorpay payment link.</h3>
            <p className="text-sm text-white/50 mt-1">Auto-invoice number, expiry, per-branch logo, partial pay — all in one form.</p>
          </div>
          <a href="/dashboard/payment-links" className="ldc-btn-primary px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2" data-testid="dashboard-create-link-cta">
            <Sparkles size={16}/> New Payment Link
          </a>
        </div>
      </div>
    </div>
  );
}
